<script lang="ts">
	import { locale, localizeHref } from '$lib/localization/locale';
	import { message } from '$lib/localization/messages';

	import { onMount } from 'svelte';
	import { goto } from '$app/navigation';
	import { analytics } from '$lib/analytics';
	import {
		signInWithPopup,
		signInWithEmailAndPassword,
		createUserWithEmailAndPassword,
		getRedirectResult,
		getAdditionalUserInfo
	} from 'firebase/auth';
	import type { User } from 'firebase/auth';
	import { auth, googleProvider } from '$lib/firebase';
	import { createDialog } from 'svelte-headlessui';
	import Transition from 'svelte-transition';
	import { browser } from '$app/environment';
	import { disposableEmailDomains } from '$lib/disposableEmailDomains';
	import { logLandingBoot } from '$lib/landingBootLog';
	import { requestVerificationEmail } from '$lib/authEmailsClient';

	logLandingBoot('LoginOrSignUpModal: component script evaluated');

	export let currentUser: User | undefined | false = false;

	export let redirectAfterLogin: string | undefined = undefined;
	export let whichModal: 'login' | 'sign-up' = 'login';
	export let loginTitleMessage: string = 'Log in to Iliad to continue.';
	export let signUpTitleMessage: string = 'Create a free account to use Iliad.';
	export let onAuthenticated:
		| ((user: User, isNewUser: boolean) => void | Promise<void>)
		| undefined = undefined;

	let email: string = '';
	let password: string = '';
	let errorMessage: string = '';

	const dialog = createDialog({
		label: message($locale, 'common/LoginOrSignUpModal', 'loginOrSignUp')
	});

	$: {
		if (browser) {
			if ($dialog.expanded) {
				document.body.style.overflowY = 'hidden';
			} else {
				document.body.style.overflowY = 'auto';
			}
		}
	}

	export function openDialog() {
		console.log('opening dialog');
		dialog.open();
	}

	export function closeDialog() {
		console.log('closing dialog');
		dialog.close();
	}

	async function completeAuthentication(user: User, isNewUser: boolean): Promise<void> {
		// Authentication may complete without changing routes, notably when a
		// signed-out snapshot viewer logs in so they can retry duplication. Close
		// the authenticated spinner before any optional callback or navigation;
		// navigating to the current URL does not remount this component.
		dialog.close();
		if (onAuthenticated) {
			await onAuthenticated(user, isNewUser);
			return;
		}
		if (redirectAfterLogin) {
			await goto(localizeHref($locale, redirectAfterLogin));
			return;
		}
	}

	// Authentication can be opened from controls inside transformed editor
	// overlays. Move the modal to <body> so its fixed positioning is always
	// relative to the viewport rather than to the transformed ancestor.
	function portal(node: HTMLElement): { destroy: () => void } {
		document.body.appendChild(node);
		return {
			destroy() {
				if (node.parentNode) node.parentNode.removeChild(node);
			}
		};
	}

	async function loginWithGoogle() {
		try {
			const result = await signInWithPopup(auth, googleProvider);
			const additionalUserInfo = getAdditionalUserInfo(result);
			const isNewUser = additionalUserInfo?.isNewUser ?? false;
			if (additionalUserInfo) {
				if (additionalUserInfo.isNewUser) {
					analytics.capture('sign_up', { method: 'google' });
				} else {
					analytics.capture('login', { method: 'google' });
				}
			}
			await completeAuthentication(result.user, isNewUser);
		} catch (error) {
			console.log('loginWithGoogle error', error);
			errorMessage = message($locale, 'common/LoginOrSignUpModal', 'failedToLogInWithGoogle');
		}
	}

	async function loginWithEmailPassword() {
		try {
			const result = await signInWithEmailAndPassword(auth, email, password);
			analytics.capture('login', { method: 'email' });
			await completeAuthentication(result.user, false);
		} catch (error) {
			console.log('loginWithEmailPassword error', error);
			errorMessage = message($locale, 'common/LoginOrSignUpModal', 'invalidEmailOrPassword');
		}
	}

	async function signUpWithEmailAndPassword() {
		try {
			const domain = email.split('@')[1];
			if (disposableEmailDomains.has(domain)) {
				errorMessage = message(
					$locale,
					'common/LoginOrSignUpModal',
					'disposableEmailAddressesAreNotAllowed'
				);
				return;
			}

			const userCredential = await createUserWithEmailAndPassword(auth, email, password);
			const user = userCredential.user;

			try {
				await requestVerificationEmail(user, $locale);
				errorMessage = message(
					$locale,
					'common/LoginOrSignUpModal',
					'verificationEmailSentPleaseCheckYourInbox'
				);
			} catch (error) {
				console.error('Could not send verification email after sign-up.', error);
				errorMessage = message(
					$locale,
					'common/LoginOrSignUpModal',
					'yourAccountWasCreatedButTheVerificationEmailCould'
				);
			}

			analytics.capture('sign_up', { method: 'email' });
			await completeAuthentication(user, true);
		} catch (error) {
			if (error instanceof Error && 'code' in error) {
				const firebaseError = error as { code: string };

				switch (firebaseError.code) {
					case 'auth/email-already-in-use':
						errorMessage = message(
							$locale,
							'common/LoginOrSignUpModal',
							'anAccountWithThisEmailAlreadyExists'
						);
						break;
					case 'auth/invalid-email':
						errorMessage = message(
							$locale,
							'common/LoginOrSignUpModal',
							'pleaseEnterAValidEmailAddress'
						);
						break;
					case 'auth/weak-password':
						errorMessage = message(
							$locale,
							'common/LoginOrSignUpModal',
							'yourPasswordIsTooWeakTryAStrongerOne'
						);
						break;
					default:
						console.log('firebaseError', firebaseError.code);
						errorMessage = message(
							$locale,
							'common/LoginOrSignUpModal',
							'somethingWentWrongPleaseTryAgain'
						);
				}
			} else {
				errorMessage = message($locale, 'common/LoginOrSignUpModal', 'anUnexpectedErrorOccurred');
			}
		}
	}

	onMount(async () => {
		const startedAt = performance.now();
		logLandingBoot('LoginOrSignUpModal: onMount start');
		console.log('login onMount');

		auth.onAuthStateChanged(async (user) => {
			logLandingBoot('LoginOrSignUpModal: auth state changed', {
				hasUser: !!user
			});
			try {
				const redirectStartedAt = performance.now();
				const redirectResult = await getRedirectResult(auth);
				logLandingBoot('LoginOrSignUpModal: redirect result checked', {
					duration: Number((performance.now() - redirectStartedAt).toFixed(1)),
					hasRedirectResult: !!redirectResult
				});
				console.log('redirectResult', redirectResult);
			} catch (error) {
				console.log('redirect result error', error);
			}

			currentUser = user ?? undefined;
			if (user) {
				if (user.email) {
					analytics.identify(user.email, {
						email: user.email,
						name: user.displayName
					});
				}
			}
		});
		logLandingBoot('LoginOrSignUpModal: onMount complete', {
			duration: Number((performance.now() - startedAt).toFixed(1))
		});
	});
</script>

<div use:portal class={`fixed w-screen h-screen inset-0 ${$dialog.expanded ? 'z-50' : 'z-[-10]'}`}>
	<Transition show={$dialog.expanded}>
		<button
			aria-label={message($locale, 'common/LoginOrSignUpModal', 'closeLoginOrSignUpModal')}
			class="w-full h-full bg-zinc-900 bg-opacity-25 cursor-default"
			on:click={dialog.close}
		></button>
		<div
			class="scroller fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-fit h-fit max-w-[calc(100vw-2rem)] max-h-[calc(100dvh-2rem)] overflow-y-auto p-6 sm:p-12 flex flex-col justify-center items-center
			rounded border shadow-md z-50 text-xs
			bg-primary-50 text-zinc-900 border-primary-200
			dark:bg-zinc-900 dark:text-zinc-200 dark:border-zinc-800"
			use:dialog.modal
		>
			<div
				class="w-[16rem] h-[32rem] sm:w-[22rem] sm:h-[36rem]
					flex flex-col justify-center items-center absolute-center-on-large"
			>
				{#if currentUser === false || currentUser}
					<div class="w-fit h-fit text-primary-600 dark:text-primary-600">
						<i class="fa-solid fa-circle-notch animate-spin text-4xl"></i>
					</div>
				{/if}

				{#if currentUser === undefined}
					<div class="w-fit h-fit p-8 flex flex-col justify-center items-center">
						<img
							loading="lazy"
							class="w-24 h-24 rounded"
							src="https://imagedelivery.net/vSd3Qxd5RvTJoQinGHZ4vg/bfb4d31d-df4e-4c8f-1717-624ca1343800/smaller"
							alt="Iliad mark"
						/>
						<div class="h-8"></div>
						<div class="text-xl text-center">
							{whichModal === 'login' ? loginTitleMessage : signUpTitleMessage}
						</div>
						<div class="h-8"></div>
						<button
							class="
				w-72 h-12 px-3 flex flex-row justify-center items-center text-sm font-semibold
				rounded shadow-lg text-zinc-50 bg-zinc-900 hover:bg-zinc-800
				dark:text-zinc-900 dark:bg-zinc-50 dark:hover:bg-zinc-200"
							on:click={loginWithGoogle}
						>
							<i class="mr-5 fa-brands fa-google"></i>
							{message($locale, 'common/LoginOrSignUpModal', 'continueWithGoogle')}
						</button>
						<div class="h-4"></div>
						<div class="text-sm text-zinc-400">
							{message($locale, 'common/LoginOrSignUpModal', 'or')}
						</div>
						<div class="h-4"></div>
						{#if whichModal === 'login'}
							<input
								type="email"
								class="w-72 h-12 px-2 bg-zinc-50 border border-primary-200 dark:bg-navy-900 dark:border-zinc-700 text-sm rounded"
								placeholder={message($locale, 'common/LoginOrSignUpModal', 'email')}
								autocomplete="username"
								bind:value={email}
							/>
							<div class="h-3"></div>
							<input
								type="password"
								class="w-72 h-12 px-2 bg-zinc-50 border border-primary-200 dark:bg-navy-900 dark:border-zinc-700 text-sm rounded"
								placeholder={message($locale, 'common/LoginOrSignUpModal', 'password')}
								autocomplete="current-password"
								bind:value={password}
							/>
							<div class="h-3"></div>
							<button
								class="
					w-72 h-12 px-3 flex flex-row justify-center items-center text-sm font-semibold
					rounded shadow-sm
					bg-primary-100 hover:brightness-90
					dark:bg-zinc-800 dark:hover:bg-zinc-700 dark:border dark:border-zinc-700 dark:hover:border-zinc-600
				"
								on:click={loginWithEmailPassword}
							>
								{whichModal === 'login'
									? message($locale, 'common/LoginOrSignUpModal', 'logIn')
									: message($locale, 'common/LoginOrSignUpModal', 'createAccount')}
							</button>
						{:else}
							<form on:submit|preventDefault={signUpWithEmailAndPassword} class="flex flex-col">
								<input
									type="email"
									class="w-72 h-12 px-2 bg-zinc-50 border border-primary-200 dark:bg-navy-900 dark:border-zinc-700 text-sm rounded"
									placeholder={message($locale, 'common/LoginOrSignUpModal', 'email')}
									autocomplete="username"
									bind:value={email}
									required
								/>
								<div class="h-3"></div>
								<input
									type="password"
									class="w-72 h-12 px-2 bg-zinc-50 border border-primary-200 dark:bg-navy-900 dark:border-zinc-700 text-sm rounded"
									placeholder={message($locale, 'common/LoginOrSignUpModal', 'password')}
									autocomplete="new-password"
									bind:value={password}
									required
								/>
								<div class="h-3"></div>
								<button
									type="submit"
									class="
						w-72 h-12 px-3 flex flex-row justify-center items-center text-sm font-semibold
						rounded shadow-sm
						bg-primary-100 hover:brightness-90
						dark:bg-zinc-800 dark:hover:bg-zinc-700 dark:border dark:border-zinc-700 dark:hover:border-zinc-600
					"
								>
									{message($locale, 'common/LoginOrSignUpModal', 'createAccount')}
								</button>
							</form>
						{/if}
						<div class="h-6"></div>
						{#if errorMessage !== ''}
							<div class="text-sm text-center text-rose-400 dark:text-rose-300">{errorMessage}</div>
							<div class="h-4"></div>
						{/if}
						{#if whichModal === 'login'}
							<div class="h-4 text-sm">
								<a
									class="text-cyan-600 hover:text-cyan-500 dark:text-cyan-400 dark:hover:text-cyan-300"
									href={localizeHref($locale, '/reset-password')}
									>{message($locale, 'common/LoginOrSignUpModal', 'resetPassword')}</a
								>
							</div>
							<div class="h-4"></div>
						{:else}
							<div class="h-4"></div>
							<div class="h-4"></div>
						{/if}
						{#if whichModal === 'login'}
							<div class="text-sm">
								{message($locale, 'common/LoginOrSignUpModal', 'noAccount')}
								<button
									class="text-cyan-600 hover:text-cyan-500 dark:text-cyan-400 dark:hover:text-cyan-300"
									on:pointerup={() => (whichModal = 'sign-up')}
								>
									{message($locale, 'common/LoginOrSignUpModal', 'createOne')}
								</button>
							</div>
						{:else}
							<div class="text-sm">
								{message($locale, 'common/LoginOrSignUpModal', 'alreadyHaveAnAccount')}
								<button
									class="text-cyan-600 hover:text-cyan-500 dark:text-cyan-400 dark:hover:text-cyan-300"
									on:pointerup={() => (whichModal = 'login')}
								>
									{message($locale, 'common/LoginOrSignUpModal', 'logIn')}
								</button>
							</div>
						{/if}
					</div>
				{/if}
			</div>
		</div>
	</Transition>
</div>
